import { getLocalDateString, parseLocalDate } from './dateUtils';
import { inviteSortDate } from './comingUpInvites';
import { distanceKm } from './tripAwareLabels';

/**
 * The ONE reading of the viewer's plans as Home sees them (the Home refresh,
 * Direction D, 2026-11-01): which trip takes the hero, what stage every plan
 * is in, the one fact that matters at that stage, and the order of the rail
 * under the hero. Pure and unit-tested; the hero, the rail and the
 * "While you're in" chips render what this returns.
 *
 * The rule the page is built on: the top of Home never depends on how much
 * the user has planned. One trip or five, the hero is full; with nothing
 * planned it becomes the home city this weekend (kind 'home').
 *
 * Stages are DERIVED, never stored:
 *   idea       no dates yet
 *   planning   dated, some day still has no stop (or no stop at all)
 *   ready      dated, every day holds a stop
 *   happening  today is inside the trip
 *   ended      over within RECAP_WINDOW_DAYS and the recap is still owed
 *   past       anything older or already recapped — dropped from Home
 *
 * The hero ladder (Trips-Rail board): happening > just ended (one day, "How
 * was Lisbon?") > an invitation waiting for your answer (someone else is
 * waiting) > the nearest dated trip > the idea you touched last > home.
 *
 * Trips arrive from /api/trips/user/me/plans (homePlansService): the lean
 * list plus day_count, planned_day_count, first_empty_day, stay, destination,
 * legs and updated_at.
 */

export const RECAP_WINDOW_DAYS = 7;
export const RAIL_LIMIT = 4;

const dayDiff = (a, b) => Math.round((parseLocalDate(a) - parseLocalDate(b)) / 86400000);

/** Is this trip's recap still owed? Day outs are rated per day, not as a trip. */
export function recapOwed(trip) {
  return !trip.myFeedbackCompleted && !trip.myReviewSkipped
    && (trip.tripType || trip.trip_type) !== 'day_out';
}

export function tripStage(trip, today) {
  const start = trip.startDate || trip.start_date || null;
  const end = trip.endDate || trip.end_date || start;
  if (!start && !end) return 'idea';
  const first = (start || end).slice(0, 10);
  const last = (end || start).slice(0, 10);
  if (last < today) {
    const since = dayDiff(today, last);
    return since <= RECAP_WINDOW_DAYS && recapOwed(trip) ? 'ended' : 'past';
  }
  if (first <= today) return 'happening';
  const dayCount = trip.day_count || 0;
  const planned = trip.planned_day_count || 0;
  return dayCount > 0 && planned >= dayCount ? 'ready' : 'planning';
}

/** The number of days a dated trip spans (1 for a single day). */
export function tripLength(trip) {
  const start = trip.startDate || trip.start_date;
  const end = trip.endDate || trip.end_date || start;
  if (!start) return trip.day_count || 0;
  return Math.max(1, dayDiff(end.slice(0, 10), start.slice(0, 10)) + 1);
}

/** 1-based day number of a date inside the trip, or null outside it. */
export function tripDayNumber(trip, dateStr) {
  const start = trip.startDate || trip.start_date;
  if (!start || !dateStr) return null;
  const n = dayDiff(dateStr.slice(0, 10), start.slice(0, 10)) + 1;
  return n >= 1 && n <= tripLength(trip) ? n : null;
}

/**
 * The day an idea from "While you're in" should land on when it has no date
 * of its own (a place, a walk): the first EMPTY day that is today or later
 * (never a day already gone on a trip under way), else today while the trip
 * is happening, else the trip's first day. Returns a 'YYYY-MM-DD' string, or
 * null for an undated trip.
 */
export function ideaTargetDate(trip, today) {
  const start = (trip.startDate || trip.start_date || '').slice(0, 10);
  if (!start) return null;
  const end = (trip.endDate || trip.end_date || start).slice(0, 10);
  const floor = today > start ? today : start;
  if (floor > end) return null;
  const empty = (trip.empty_day_dates || []).map(d => String(d).slice(0, 10)).filter(d => d >= floor).sort();
  if (empty[0]) return empty[0];
  if (trip.first_empty_day?.date && String(trip.first_empty_day.date).slice(0, 10) >= floor) return trip.first_empty_day.date.slice(0, 10);
  return floor;
}

/** The cover Home shows for a trip: the trip's own, else the viewer's album, else the destination's. */
export function tripCover(trip) {
  return trip.cover_photo
    || trip.album_cover
    || trip.tripCardPhotos?.[0]?.url
    || trip.destinationCoverPhoto?.photo_url
    || trip.destinationCoverPhoto?.thumb_url
    || null;
}

/**
 * The photos the hero drifts through on a trip under way or just over: the
 * cover first, then up to `limit - 1` of the trip's own photos (the plans
 * read's `hero_photos`, already gated per viewer), shuffled by `random` so a
 * return visit doesn't replay the same order. One photo, or none, means no
 * rotation.
 */
export function heroPhotos(trip, limit = 5, random = Math.random) {
  const cover = tripCover(trip);
  const seen = new Set(cover ? [cover] : []);
  const rest = (trip.hero_photos || []).filter(url => url && !seen.has(url) && seen.add(url));
  for (let i = rest.length - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1));
    [rest[i], rest[j]] = [rest[j], rest[i]];
  }
  return [...(cover ? [cover] : []), ...rest].slice(0, limit);
}

const hasCoords = (p) => !!p && Number.isFinite(p.lat) && Number.isFinite(p.lng);
const covers = (row, today) => {
  const start = String(row.start_date || row.startDate || '').slice(0, 10);
  const end = String(row.end_date || row.endDate || start).slice(0, 10);
  return !!start && start <= today && today <= end;
};

/**
 * Where "While you're in" centres for this trip: the stay when it has
 * coordinates (near where you sleep), else the primary destination, else the
 * first leg with coordinates. On a trip under way it is TODAY's stay or leg
 * (Lisbon days 1–3, Porto days 4–7: on day 5 the centre is Porto), so the
 * ideas are for where you are, not where you started. Null when the trip
 * has no place at all.
 */
export function tripCenter(trip, today = getLocalDateString(new Date())) {
  // The centre's name is the PLACE ("Lisbon"), never the stay's: it titles
  // "While you're in …"; the stay is named by the subtitle.
  const stays = Array.isArray(trip.stays) && trip.stays.length ? trip.stays : (trip.stay ? [trip.stay] : []);
  const legs = (trip.legs || []).filter(hasCoords);
  if (tripStage(trip, today) === 'happening') {
    const todaysLeg = legs.filter(l => covers(l, today)).pop() || null;
    const todaysStay = stays.filter(hasCoords).find(s => covers(s, today)) || null;
    if (todaysStay) {
      return { lat: todaysStay.lat, lng: todaysStay.lng, name: todaysLeg?.place?.split(',')[0].trim() || tripPlaceName(trip), kind: 'stay', stay: todaysStay };
    }
    if (todaysLeg) return { lat: todaysLeg.lat, lng: todaysLeg.lng, name: todaysLeg.place.split(',')[0].trim(), kind: 'leg' };
  }
  const stay = stays.find(hasCoords);
  if (stay) return { lat: stay.lat, lng: stay.lng, name: tripPlaceName(trip), kind: 'stay', stay };
  const dest = trip.destination;
  if (hasCoords(dest)) {
    return { lat: dest.lat, lng: dest.lng, name: dest.name || tripPlaceName(trip), kind: 'destination' };
  }
  const leg = legs[0];
  if (leg) return { lat: leg.lat, lng: leg.lng, name: (leg.place || tripPlaceName(trip)).split(',')[0].trim(), kind: 'leg' };
  return null;
}

/**
 * The name of the trip under way at `point` — where a list fenced on the
 * phone's location is, when the phone is away from home: "Barcelona". Null
 * when no trip under way is centred within `radiusKm` (the caller says "near
 * you" instead).
 */
export function happeningPlaceAt(away, point, radiusKm) {
  if (!hasCoords(point)) return null;
  const match = (away || []).find(entry => entry.stage === 'happening' && entry.center
    && (distanceKm(point.lat, point.lng, entry.center.lat, entry.center.lng) ?? Infinity) <= radiusKm);
  return match?.center.name || null;
}

/** The place a trip is about, short: "Lisbon" from "Lisbon, Portugal". */
export function tripPlaceName(trip) {
  const raw = trip.destination?.name || trip.location || trip.name || '';
  return String(raw).split(',')[0].trim();
}

/**
 * The one fact worth saying about a plan at its stage — a key plus params for
 * the catalog (home:plans.fact.*). Empty days beat a missing stay: a day with
 * nothing in it is the thing the hero's button can fix right now.
 */
export function tripFact(trip, stage, today) {
  const stops = trip.activity_count || trip.activityCount || 0;
  switch (stage) {
    case 'idea':
      return stops > 0 ? { key: 'ideaCount', count: stops } : { key: 'idea' };
    case 'planning': {
      const dayCount = trip.day_count || tripLength(trip);
      const planned = trip.planned_day_count || 0;
      const empty = Math.max(0, dayCount - planned);
      if (planned === 0) return { key: 'nothingPlanned', count: dayCount };
      if (empty > 0 && trip.first_empty_day) {
        return empty === 1
          ? { key: 'emptyDay', index: trip.first_empty_day.index }
          : { key: 'emptyDays', count: empty, index: trip.first_empty_day.index };
      }
      if (!trip.stay) return { key: 'noStay', stops };
      return { key: 'planned', planned, count: dayCount };
    }
    case 'ready':
      return trip.stay ? { key: 'allPlanned', count: trip.day_count || tripLength(trip) } : { key: 'noStay', stops };
    case 'happening': {
      const start = (trip.startDate || trip.start_date).slice(0, 10);
      return { key: 'today', index: dayDiff(today, start) + 1, count: tripLength(trip) };
    }
    case 'ended':
      return { key: 'recapOwed' };
    default:
      return null;
  }
}

const tripStart = trip => (trip.startDate || trip.start_date || '').slice(0, 10);
const tripUpdated = trip => trip.updatedAt || trip.updated_at || trip.createdAt || trip.created_at || '';

/**
 * Every plan Home can show, staged and sorted for the rail. `trips` from the
 * plans endpoint, `invites` from fetchCurrentInvites (soonest first), and
 * `activities` the week's scheduled standalone activities (day outs).
 *
 * Returns:
 *   hero   { kind: 'happening'|'justEnded'|'invite'|'upcoming'|'idea'|'home', trip?, invite?, stage?, fact? }
 *   rail   [{ kind: 'trip'|'invite'|'activity', key, item, stage?, fact?, sortDate }]
 *          — every plan but the hero's, in rail order (happening, invites,
 *          dated by date, ideas by last touch, recap owed last)
 *   away   trips with a place for "While you're in", hero's trip first
 */
export function buildPlans({ trips = [], invites = [], activities = [], today = getLocalDateString(new Date()) } = {}) {
  const staged = trips
    .map(trip => ({ trip, stage: tripStage(trip, today) }))
    .filter(entry => entry.stage !== 'past')
    .map(entry => ({ ...entry, fact: tripFact(entry.trip, entry.stage, today) }));

  const confirmedTripIds = new Set(staged.map(e => e.trip.id));
  // An invite for a trip you're already on (a child's invitation still open)
  // is not a second plan; it stays in the planner's invitations list.
  const openInvites = invites.filter(inv => !(inv.type === 'trip' && confirmedTripIds.has(inv.id)));

  const byStart = (a, b) => tripStart(a.trip).localeCompare(tripStart(b.trip));
  const happening = staged.filter(e => e.stage === 'happening').sort(byStart);
  const justEnded = staged
    .filter(e => e.stage === 'ended' && dayDiff(today, (e.trip.endDate || e.trip.end_date || e.trip.startDate || e.trip.start_date).slice(0, 10)) <= 1)
    .sort((a, b) => tripStart(b.trip).localeCompare(tripStart(a.trip)));
  const upcoming = staged.filter(e => e.stage === 'planning' || e.stage === 'ready').sort(byStart);
  const ideas = staged.filter(e => e.stage === 'idea')
    .sort((a, b) => String(tripUpdated(b.trip)).localeCompare(String(tripUpdated(a.trip))));

  let hero;
  if (happening[0]) hero = { kind: 'happening', ...happening[0] };
  else if (justEnded[0]) hero = { kind: 'justEnded', ...justEnded[0] };
  else if (openInvites[0]) hero = { kind: 'invite', invite: openInvites[0] };
  else if (upcoming[0]) hero = { kind: 'upcoming', ...upcoming[0] };
  else if (ideas[0]) hero = { kind: 'idea', ...ideas[0] };
  else hero = { kind: 'home' };

  const heroTripId = hero.trip?.id;
  const heroInvite = hero.invite;

  const railTrips = staged
    .filter(e => e.trip.id !== heroTripId)
    .map(e => ({ kind: 'trip', key: `trip-${e.trip.id}`, item: e.trip, stage: e.stage, fact: e.fact,
      sortDate: tripStart(e.trip) || null }));
  const railInvites = openInvites
    .filter(inv => !(heroInvite && heroInvite.type === inv.type && heroInvite.id === inv.id))
    .map(inv => ({ kind: 'invite', key: `invite-${inv.type}-${inv.id}`, item: inv,
      sortDate: getLocalDateString(inviteSortDate(inv)) }));
  const railActivities = activities.map(a => ({
    kind: 'activity', key: `activity-${a.id}`, item: a,
    sortDate: (a.scheduled_date || a.scheduledDate || '').slice(0, 10) || null
  }));

  const group = entry => {
    if (entry.kind === 'trip' && entry.stage === 'happening') return 0;
    if (entry.kind === 'invite') return 1;
    if (entry.kind === 'trip' && entry.stage === 'idea') return 3;
    if (entry.kind === 'trip' && entry.stage === 'ended') return 4;
    return 2;
  };
  const rail = [...railTrips, ...railInvites, ...railActivities].sort((a, b) => {
    const g = group(a) - group(b);
    if (g !== 0) return g;
    if (group(a) === 3) return String(tripUpdated(b.item)).localeCompare(String(tripUpdated(a.item)));
    return String(a.sortDate || '9999').localeCompare(String(b.sortDate || '9999'));
  });

  const away = [
    ...(hero.trip && hero.stage !== 'ended' ? [hero] : []),
    ...upcoming.filter(e => e.trip.id !== heroTripId),
    ...happening.filter(e => e.trip.id !== heroTripId),
    ...ideas.filter(e => e.trip.id !== heroTripId)
  ]
    .map(e => ({ trip: e.trip, stage: e.stage, center: tripCenter(e.trip, today) }))
    .filter(e => e.center);

  return { hero, rail, away };
}
